/**
 * React resets a controlled `<input>`'s cursor to the END whenever its
 * `value` prop is rewritten to something the browser didn't produce
 * verbatim from the user's own keystroke — which is exactly what every
 * onChange transform in this app does (force-uppercase, strip non-digits,
 * auto-insert phone dashes). Without this, typing/editing anywhere but
 * the very end of a field bounces the cursor to the end after every
 * keystroke. Both helpers below mutate the DOM node's value/selection
 * SYNCHRONOUSLY, inside the same change-event handler that calls the
 * transform — React's controlled-input reconciliation only rewrites
 * `.value` (and resets the cursor) when the incoming prop actually
 * differs from what's already in the DOM, so a DOM already showing the
 * post-transform value/cursor makes that later re-render a no-op.
 */

/** For a transform that changes case/characters but never the LENGTH or
 * ORDER of characters (e.g. `.toUpperCase()`) — the cursor's index is
 * unchanged, so this just restores the exact same selection. */
export function applySameLengthTransform(input: HTMLInputElement | HTMLTextAreaElement, transform: (raw: string) => string): string {
  const { selectionStart, selectionEnd } = input;
  const next = transform(input.value);
  input.value = next;
  if (selectionStart != null && selectionEnd != null) input.setSelectionRange(selectionStart, selectionEnd);
  return next;
}

/** For a transform that only reorders/adds/removes non-digit "formatting"
 * characters around an otherwise-untouched run of digits (auto-dashed
 * phone numbers, digit-only fields that strip stray non-digit input) —
 * restores the cursor to sit after the same COUNT of digits it followed
 * before the transform ran, wherever that count of digits now lands. */
export function applyDigitPreservingTransform(input: HTMLInputElement, transform: (raw: string) => string): string {
  const pos = input.selectionStart ?? input.value.length;
  const digitsBeforeCursor = (input.value.slice(0, pos).match(/\d/g) ?? []).length;
  const next = transform(input.value);

  let newPos = next.length;
  if (digitsBeforeCursor === 0) {
    newPos = 0;
  } else {
    let seen = 0;
    for (let i = 0; i < next.length; i++) {
      if (/\d/.test(next[i]!)) {
        seen++;
        if (seen === digitsBeforeCursor) {
          newPos = i + 1;
          break;
        }
      }
    }
  }

  input.value = next;
  input.setSelectionRange(newPos, newPos);
  return next;
}

/** For a decimal-number field (prices, quantities): keeps only digits and
 * the FIRST "." — everything else typed or pasted is dropped. Cursor is
 * restored to sit after the same count of KEPT characters it followed
 * before the transform. */
export function applyDecimalTransform(input: HTMLInputElement): string {
  const pos = input.selectionStart ?? input.value.length;
  let out = "";
  let seenDot = false;
  let newPos = 0;
  for (let i = 0; i < input.value.length; i++) {
    const ch = input.value[i]!;
    const keep = /\d/.test(ch) || (ch === "." && !seenDot);
    if (!keep) continue;
    if (ch === ".") seenDot = true;
    out += ch;
    if (i < pos) newPos = out.length;
  }
  input.value = out;
  input.setSelectionRange(newPos, newPos);
  return out;
}
